import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMediaBreakpoint } from '@americanexpress/consumer-travel-base';
import clsx from 'clsx';
import { useExperimentContext } from '@americanexpress/one-experiments';
import { Modal, Surface } from '@americanexpress/dls-react';
import locationContext from '../../context/locationsContext';
import { moduleName } from '../../constants';
import LocationsSearch from './LocationsSearch';
import { HOMEPAGE_REDESIGN_EXPERIMENT } from '../../constants/experimentConstants';
import styles from './LocationsSearchWrapper.scss';
import { ESCAPE, TAB } from '../../constants/keyConstants';

export const bindClickOutside = ({ handler, ref }) => (event) => {
  if (ref?.current && !ref.current.contains(event?.target)) {
    handler(false);
  }
};

export const bindTabOutside = ({ ref, handler }) => (event) => {
  setTimeout(() => {
    // setTimeout allows us to check if another child in the wrapper has focus before closing
    // if removed, the popup may be closed whilst the user is still interacting with the input
    if (event.keyCode === TAB && ref?.current && !ref.current.contains(document.activeElement)) {
      handler(false);
    }
  }, 0);
  if (event.keyCode === ESCAPE && ref?.current) {
    handler(false);
    ref.current.focus();
  }
};

export const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const onClickCheckIfFocusIsOutsideWrapper = bindClickOutside({ handler, ref });
    document.addEventListener('mousedown', onClickCheckIfFocusIsOutsideWrapper);
    document.addEventListener('touchstart', onClickCheckIfFocusIsOutsideWrapper);
    return () => {
      document.removeEventListener('mousedown', onClickCheckIfFocusIsOutsideWrapper);
      document.removeEventListener('touchstart', onClickCheckIfFocusIsOutsideWrapper);
    };
  }, [ref, handler]);
};

export const useOnTabOutside = (ref, handler) => {
  useEffect(() => {
    const onKeyPressCheckIfFocusIsOutsideWrapper = bindTabOutside({ ref, handler });
    document.addEventListener('keydown', onKeyPressCheckIfFocusIsOutsideWrapper);
    return () => { document.removeEventListener('keydown', onKeyPressCheckIfFocusIsOutsideWrapper); };
  }, [ref, handler]);
};

const LocationsSearchWrapper = ({ className }) => {
  const {
    state: {
      isFullscreenInput,
      theme,
    },
    actions: {
      onCloseFullScreenInput,
      setShouldDisplayResults,
    },
    config: {
      labelText,
    },
  } = React.useContext(locationContext);
  const wrapperRef = React.useRef(null);
  const isMobile = useMediaBreakpoint('sm-down');
  useOnClickOutside(wrapperRef, setShouldDisplayResults);
  useOnTabOutside(wrapperRef, setShouldDisplayResults);

  const displayFullScreenView = isMobile && isFullscreenInput;

  const { selectors: { getExperimentVariant } } = useExperimentContext();
  const experimentData = getExperimentVariant(HOMEPAGE_REDESIGN_EXPERIMENT);

  const isVariantRedesign = useMemo(() => experimentData
    && experimentData.dimension === 'search_widget'
    && experimentData.variant !== 'control', [experimentData?.dimension, experimentData?.variant]);

  return (
    <div
      className={className}
      data-module-name={moduleName}
      ref={wrapperRef}
    >
      {displayFullScreenView && (
        <Modal
          data-testid="locations-input-modal"
          className={clsx([
            'scroll-y position-fixed-tl width-full height-full border-0 pad-0',
            isVariantRedesign ? 'dls-gray-01-bg anim-slide-from-right' : 'color-surface-bg',
            styles.popup,
          ])}
          style={{ zIndex: 'var(--above_C2C)' }}
          aria-labelledby="locations-input-modal-label"
          onClose={onCloseFullScreenInput}
        >
          <Surface data-testid="theme-wrapper" variant={theme === 'blue' ? 'fg-brand' : 'foreground'}>
            <span id="locations-input-modal-label" className="sr-only">{labelText}</span>
            <LocationsSearch isMobile={isMobile} isFullScreenModal={true} />
          </Surface>
        </Modal>
      )}
      <LocationsSearch isMobile={isMobile} />
    </div>
  );
};

LocationsSearchWrapper.propTypes = {
  className: PropTypes.string,
};

LocationsSearchWrapper.defaultProps = {
  className: null,
};

export { LocationsSearchWrapper as TestableLocationsSearchWrapper };
export default LocationsSearchWrapper;
